import React, { useState, useEffect } from 'react';
import { collection, getDocs, updateDoc, doc, query, where, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { FiSearch, FiAlertTriangle, FiPackage, FiTrendingDown, FiEdit2, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import { Modal, Button, Form } from 'react-bootstrap';

export default function StockManagement() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLevel, setFilterLevel] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showModal, setShowModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [adjustmentData, setAdjustmentData] = useState({
    quantity: 0,
    reason: '',
    type: 'add'
  });
  const { selectedHierarchy } = useAuth();

  useEffect(() => {
    loadProducts();
  }, [selectedHierarchy]);

  const loadProducts = async () => {
    if (!selectedHierarchy?.outlet?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const outletId = selectedHierarchy.outlet.id;
      
      // Fetch categories first
      const categoriesRef = collection(db, 'categories');
      const categoriesQuery = query(categoriesRef, where('outletId', '==', outletId));
      const categoriesSnap = await getDocs(categoriesQuery);
      
      const categoryMap = {};
      const categoriesList = [];
      categoriesSnap.docs.forEach(doc => {
        const categoryData = doc.data();
        const categoryName = categoryData.name || 'Uncategorized';
        categoryMap[doc.id] = categoryName;
        categoriesList.push({ id: doc.id, name: categoryName });
      });
      
      // Sort categories alphabetically by name
      categoriesList.sort((a, b) => a.name.localeCompare(b.name));
      setCategories(categoriesList);
      
      // Fetch products
      const productsRef = collection(db, 'products');
      const productsQuery = query(productsRef, where('outletId', '==', outletId));
      const productsSnap = await getDocs(productsQuery);
      
      // Fetch stock data (both outlet-specific and null outletId for legacy data)
      const stockQuery1 = query(collection(db, 'stock'), where('outletId', '==', outletId));
      const stockQuery2 = query(collection(db, 'stock'), where('outletId', '==', null));
      
      const [stockSnap1, stockSnap2] = await Promise.all([
        getDocs(stockQuery1),
        getDocs(stockQuery2)
      ]);
      
      // Build stock map
      const stockMap = {};
      
      // Process stock with matching outletId (priority)
      stockSnap1.docs.forEach(doc => {
        const stockData = doc.data();
        stockMap[stockData.productId] = {
          quantity: stockData.quantity || 0,
          stockDocId: doc.id
        };
      });
      
      // Process stock with null outletId (fallback for legacy data)
      stockSnap2.docs.forEach(doc => {
        const stockData = doc.data();
        if (!stockMap[stockData.productId]) {
          stockMap[stockData.productId] = {
            quantity: stockData.quantity || 0,
            stockDocId: doc.id
          };
        }
      });
      
      // Combine products with stock data
      const productsData = productsSnap.docs.map(doc => {
        const productData = doc.data();
        const stockInfo = stockMap[doc.id] || { quantity: 0, stockDocId: null };
        
        return {
          id: doc.id,
          ...productData,
          stock: stockInfo.quantity,
          stockDocId: stockInfo.stockDocId,
          category: categoryMap[productData.categoryId] || 'Uncategorized'
        };
      });
      
      setProducts(productsData);
    } catch (error) {
      console.error('Error loading products:', error);
      try {
        // Fallback: load all products, stock, and categories
        const categoriesRef = collection(db, 'categories');
        const categoriesSnap = await getDocs(categoriesRef);
        
        const categoryMap = {};
        const categoriesList = [];
        categoriesSnap.docs.forEach(doc => {
          const categoryData = doc.data();
          const categoryName = categoryData.name || 'Uncategorized';
          categoryMap[doc.id] = categoryName;
          categoriesList.push({ id: doc.id, name: categoryName });
        });
        
        // Sort categories alphabetically by name
        categoriesList.sort((a, b) => a.name.localeCompare(b.name));
        setCategories(categoriesList);
        
        const productsRef = collection(db, 'products');
        const productsSnap = await getDocs(productsRef);
        
        const stockRef = collection(db, 'stock');
        const stockSnap = await getDocs(stockRef);
        
        const stockMap = {};
        stockSnap.docs.forEach(doc => {
          const stockData = doc.data();
          if (!stockMap[stockData.productId]) {
            stockMap[stockData.productId] = {
              quantity: stockData.quantity || 0,
              stockDocId: doc.id
            };
          }
        });
        
        const productsData = productsSnap.docs.map(doc => {
          const productData = doc.data();
          const stockInfo = stockMap[doc.id] || { quantity: 0, stockDocId: null };
          
          return {
            id: doc.id,
            ...productData,
            stock: stockInfo.quantity,
            stockDocId: stockInfo.stockDocId,
            category: categoryMap[productData.categoryId] || 'Uncategorized'
          };
        });
        
        setProducts(productsData);
      } catch (fallbackError) {
        console.error('Fallback query failed:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAdjustStock = async (e) => {
    e.preventDefault();
    if (!selectedProduct) return;
    
    // SECURITY: Verify outlet is selected
    if (!selectedHierarchy?.outlet?.id) {
      alert('Error: No outlet selected. Please select an outlet first.');
      return;
    }
    
    // SECURITY: Verify the product belongs to the current outlet
    if (selectedProduct.outletId !== selectedHierarchy.outlet.id) {
      alert('Security Error: Cannot modify stock for product from another outlet.');
      return;
    }


    try {
      const currentStock = Number(selectedProduct.stock) || 0;
      const adjustmentQty = Number(adjustmentData.quantity) || 0;
      const newStock = adjustmentData.type === 'add' 
        ? currentStock + adjustmentQty 
        : currentStock - adjustmentQty;

      // Update the stock collection (not the products collection)
      if (selectedProduct.stockDocId) {
        // SECURITY: Verify the stock document belongs to the current outlet
        const stockDoc = products.find(p => p.stockDocId === selectedProduct.stockDocId);
        if (stockDoc && stockDoc.outletId !== selectedHierarchy.outlet.id) {
          alert('Security Error: Cannot modify stock from another outlet.');
          return;
        }
        
        // Update existing stock document
        await updateDoc(doc(db, 'stock', selectedProduct.stockDocId), {
          quantity: Math.max(0, newStock),
          lastUpdated: new Date(),
          stockHistory: [
            ...(selectedProduct.stockHistory || []),
            {
              date: new Date(),
              type: adjustmentData.type,
              quantity: adjustmentQty,
              reason: adjustmentData.reason,
              previousStock: currentStock,
              newStock: Math.max(0, newStock)
            }
          ]
        });
      } else {
        // Create new stock document if it doesn't exist
        await addDoc(collection(db, 'stock'), {
          productId: selectedProduct.id,
          outletId: selectedHierarchy.outlet.id,
          quantity: Math.max(0, newStock),
          lastUpdated: new Date(),
          stockHistory: [
            {
              date: new Date(),
              type: adjustmentData.type,
              quantity: adjustmentQty,
              reason: adjustmentData.reason,
              previousStock: currentStock,
              newStock: Math.max(0, newStock)
            }
          ]
        });
      }

      setShowModal(false);
      resetForm();
      loadProducts();
    } catch (error) {
      console.error('Error adjusting stock:', error);
      alert('Failed to adjust stock. Please try again.');
    }
  };

  const openAdjustmentModal = (product) => {
    setSelectedProduct(product);
    setShowModal(true);
  };

  const resetForm = () => {
    setAdjustmentData({
      quantity: 0,
      reason: '',
      type: 'add'
    });
    setSelectedProduct(null);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getStockLevel = (stock) => {
    if (stock === 0) return 'out';
    if (stock <= 10) return 'low';
    if (stock <= 50) return 'medium';
    return 'good';
  };

  const getStockBadge = (stock) => {
    const level = getStockLevel(stock);
    const colors = {
      out: 'danger',
      low: 'warning',
      medium: 'info',
      good: 'success'
    };
    const labels = {
      out: 'Out of Stock',
      low: 'Low Stock',
      medium: 'Medium',
      good: 'In Stock'
    };
    return <span className={`badge bg-${colors[level]}`}>{labels[level]}</span>;
  };

  const filteredProducts = products
    .filter(product => {
      const matchesSearch = product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           product.sku?.toLowerCase().includes(searchTerm.toLowerCase());
      
      if (!matchesSearch) return false;
      
      // Category filter
      if (categoryFilter !== 'all' && product.category !== categoryFilter) return false;
      
      // Stock level filter
      if (filterLevel === 'all') return true;
      const level = getStockLevel(product.stock || 0);
      return level === filterLevel;
    })
    .sort((a, b) => {
      let aValue, bValue;
      
      switch(sortField) {
        case 'name':
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;
        case 'sku':
          aValue = (a.sku || '').toLowerCase();
          bValue = (b.sku || '').toLowerCase();
          break;
        case 'category':
          aValue = (a.category || 'Uncategorized').toLowerCase();
          bValue = (b.category || 'Uncategorized').toLowerCase();
          break;
        case 'stock':
          aValue = Number(a.stock) || 0;
          bValue = Number(b.stock) || 0;
          break;
        case 'price':
          aValue = Number(a.price) || 0;
          bValue = Number(b.price) || 0;
          break;
        case 'value':
          aValue = (Number(a.stock) || 0) * (Number(a.price) || 0);
          bValue = (Number(b.stock) || 0) * (Number(b.price) || 0);
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  const lowStockCount = products.filter(p => getStockLevel(p.stock || 0) === 'low').length;
  const outOfStockCount = products.filter(p => getStockLevel(p.stock || 0) === 'out').length;
  const totalStock = products.reduce((sum, p) => sum + (Number(p.stock) || 0), 0);
  const totalValue = products.reduce((sum, p) => sum + ((Number(p.stock) || 0) * (Number(p.price) || 0)), 0);

  return (
    <div className="container-fluid">
      <div className="page-header mb-4">
        <h1 className="page-title">Stock Management</h1>
        <p className="page-subtitle">Monitor and manage inventory stock levels</p>
      </div>

      {/* Stats Cards */}
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="card hover-lift">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1 small">Total Products</p>
                  <h3 className="mb-0">{products.length}</h3>
                </div>
                <div className="stat-card-icon" style={{ background: 'var(--primary-gradient)', width: '48px', height: '48px' }}>
                  <FiPackage className="text-white" size={24} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card hover-lift">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1 small">Total Stock</p>
                  <h3 className="mb-0">{totalStock}</h3>
                </div>
                <div className="stat-card-icon" style={{ background: 'var(--success-gradient)', width: '48px', height: '48px' }}>
                  <i className="bi bi-box-seam text-white" style={{ fontSize: '24px' }}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card hover-lift">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1 small">Low Stock</p>
                  <h3 className="mb-0 text-warning">{lowStockCount}</h3>
                </div>
                <div className="stat-card-icon" style={{ background: 'var(--warning-gradient)', width: '48px', height: '48px' }}>
                  <FiTrendingDown className="text-white" size={24} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card hover-lift">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1 small">Out of Stock</p>
                  <h3 className="mb-0 text-danger">{outOfStockCount}</h3>
                </div>
                <div className="stat-card-icon" style={{ background: 'var(--danger-gradient)', width: '48px', height: '48px' }}>
                  <FiAlertTriangle className="text-white" size={24} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stock Table */}
      <div className="card hover-lift">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Stock Management</h5>
          <button className="btn btn-sm btn-outline-primary">
            <i className="bi bi-download me-2"></i>
            Export Report
          </button>
        </div>
        
        {/* Filter Controls */}
        {!loading && products.length > 0 && (
          <div className="card-body border-bottom">
            <div className="row g-3 align-items-end">
              <div className="col-md-4">
                <label className="form-label small text-muted mb-1">Search</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label small text-muted mb-1">Category</label>
                <select 
                  className="form-select form-select-sm"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <option value="all">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                  ))}
                  <option value="Uncategorized">Uncategorized</option>
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label small text-muted mb-1">Stock Level</label>
                <select 
                  className="form-select form-select-sm"
                  value={filterLevel}
                  onChange={(e) => setFilterLevel(e.target.value)}
                >
                  <option value="all">All Stock Levels</option>
                  <option value="out">Out of Stock</option>
                  <option value="low">Low Stock</option>
                  <option value="medium">Medium Stock</option>
                  <option value="good">Good Stock</option>
                </select>
              </div>
              <div className="col-md-2">
                <button
                  className="btn btn-sm btn-outline-secondary w-100"
                  onClick={() => {
                    setSearchTerm('');
                    setCategoryFilter('all');
                    setFilterLevel('all');
                  }}
                >
                  <i className="bi bi-x-circle me-1"></i>
                  Clear
                </button>
              </div>
            </div>
            {(searchTerm || categoryFilter !== 'all' || filterLevel !== 'all') && (
              <div className="mt-2">
                <small className="text-muted">
                  Showing {filteredProducts.length} of {products.length} products
                </small>
              </div>
            )}
          </div>
        )}

        <div className="card-body">
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mt-2">Loading stock data...</p>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-5">
              <FiPackage size={48} className="text-muted mb-3" />
              <p className="text-muted mb-0">
                {searchTerm || categoryFilter !== 'all' || filterLevel !== 'all' 
                  ? 'No products match your filters.' 
                  : 'No products found.'}
              </p>
              {(searchTerm || categoryFilter !== 'all' || filterLevel !== 'all') && (
                <button
                  className="btn btn-sm btn-outline-primary mt-2"
                  onClick={() => {
                    setSearchTerm('');
                    setCategoryFilter('all');
                    setFilterLevel('all');
                  }}
                >
                  Clear Filters
                </button>
              )}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th 
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('name')}
                    >
                      Product
                      {sortField === 'name' && (
                        sortDirection === 'asc' ? <FiArrowUp className="ms-1" size={14} /> : <FiArrowDown className="ms-1" size={14} />
                      )}
                    </th>
                    <th 
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('sku')}
                    >
                      SKU
                      {sortField === 'sku' && (
                        sortDirection === 'asc' ? <FiArrowUp className="ms-1" size={14} /> : <FiArrowDown className="ms-1" size={14} />
                      )}
                    </th>
                    <th 
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('category')}
                    >
                      Category
                      {sortField === 'category' && (
                        sortDirection === 'asc' ? <FiArrowUp className="ms-1" size={14} /> : <FiArrowDown className="ms-1" size={14} />
                      )}
                    </th>
                    <th 
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('stock')}
                    >
                      Current Stock
                      {sortField === 'stock' && (
                        sortDirection === 'asc' ? <FiArrowUp className="ms-1" size={14} /> : <FiArrowDown className="ms-1" size={14} />
                      )}
                    </th>
                    <th>Stock Level</th>
                    <th 
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('price')}
                    >
                      Price
                      {sortField === 'price' && (
                        sortDirection === 'asc' ? <FiArrowUp className="ms-1" size={14} /> : <FiArrowDown className="ms-1" size={14} />
                      )}
                    </th>
                    <th 
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('value')}
                    >
                      Stock Value
                      {sortField === 'value' && (
                        sortDirection === 'asc' ? <FiArrowUp className="ms-1" size={14} /> : <FiArrowDown className="ms-1" size={14} />
                      )}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map(product => {
                    const stock = Number(product.stock) || 0;
                    const price = Number(product.price) || 0;
                    const value = stock * price;
                    
                    return (
                      <tr key={product.id}>
                        <td className="fw-semibold">{product.name}</td>
                        <td className="text-muted">{product.sku || '-'}</td>
                        <td>
                          <small className="text-muted">{product.category || 'Uncategorized'}</small>
                        </td>
                        <td>
                          <span className="fw-bold">{stock}</span>
                        </td>
                        <td>{getStockBadge(stock)}</td>
                        <td>{price.toFixed(2)}</td>
                        <td className="fw-semibold">{value.toFixed(2)}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => openAdjustmentModal(product)}
                            title="Adjust Stock"
                          >
                            <FiEdit2 className="me-1" size={14} />
                            Adjust
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-light">
                  <tr>
                    <th colSpan="6" className="text-end">Total Inventory Value:</th>
                    <th colSpan="2">{totalValue.toFixed(2)}</th>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Stock Adjustment Modal */}
      <Modal show={showModal} onHide={() => { setShowModal(false); resetForm(); }}>
        <Modal.Header closeButton>
          <Modal.Title>Adjust Stock - {selectedProduct?.name}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAdjustStock}>
          <Modal.Body>
            <div className="mb-3">
              <p className="mb-2">
                <strong>Current Stock:</strong> <span className="badge bg-primary">{selectedProduct?.stock || 0}</span>
              </p>
            </div>
            
            <Form.Group className="mb-3">
              <Form.Label>Adjustment Type *</Form.Label>
              <Form.Select
                value={adjustmentData.type}
                onChange={(e) => setAdjustmentData({ ...adjustmentData, type: e.target.value })}
                required
              >
                <option value="add">Add Stock</option>
                <option value="remove">Remove Stock</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Quantity *</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={adjustmentData.quantity}
                onChange={(e) => setAdjustmentData({ ...adjustmentData, quantity: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Reason *</Form.Label>
              <Form.Select
                value={adjustmentData.reason}
                onChange={(e) => setAdjustmentData({ ...adjustmentData, reason: e.target.value })}
                required
              >
                <option value="">Select reason...</option>
                <option value="purchase">New Purchase</option>
                <option value="return">Customer Return</option>
                <option value="damage">Damaged/Lost</option>
                <option value="sale">Sale</option>
                <option value="inventory">Inventory Count</option>
                <option value="other">Other</option>
              </Form.Select>
            </Form.Group>

            <div className="alert alert-info small">
              <strong>New Stock:</strong> {
                adjustmentData.type === 'add' 
                  ? (Number(selectedProduct?.stock || 0) + Number(adjustmentData.quantity || 0))
                  : Math.max(0, Number(selectedProduct?.stock || 0) - Number(adjustmentData.quantity || 0))
              }
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => { setShowModal(false); resetForm(); }}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Update Stock
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
